"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createElasticsearchAdapter = void 0;
const common_1 = require("@mods-dev/common");
const utils_1 = require("./utils");
const BOOST_NAME_EXACT = 10;
const BOOST_NAME_WILDCARD = 7.5;
const BOOST_KEYWORD = 5;
const BOOST_OWNER = 2;
const BOOST_README = 1;
const MAX_SEARCH_BOOST = 20;
const MAX_RELATED_BOOST = 10;
const MAX_RELATED_RESULTS = Math.ceil(common_1.MAX_RESULTS / 2);
function createElasticsearchAdapter(props) {
    return {
        store: store(props),
        find: find(props),
        search: search(props),
        stats: stats(props),
    };
}
exports.createElasticsearchAdapter = createElasticsearchAdapter;
function byScore(a, b) {
    if (a.scores.score * a._score > b.scores.score * b._score) {
        return -1;
    }
    if (a.scores.score * a._score < b.scores.score * b._score) {
        return 1;
    }
    return 0;
}
function unique(mod, index, self) {
    return self.findIndex((item) => item.name === mod.name) === index;
}
const store = ({ elasticsearch, logger }) => async (ecosystem, name, data) => {
    logger.info(`[data-layer] Storing ${ecosystem}@${name}`);
    elasticsearch.index({
        index: ecosystem,
        id: name,
        body: data,
    });
};
const find = ({ elasticsearch, logger }) => async (ecosystem, name) => {
    logger.info(`[data-layer] Finding ${ecosystem}@${name}`);
    const response = await elasticsearch.get({
        index: ecosystem,
        id: name,
    });
    return response.body._source || null;
};
const search = ({ elasticsearch, logger }) => async (ecosystem, term) => {
    logger.info(`[data-layer] Searching ${ecosystem}@${term}`);
    /**
     * Algorithm:
     * 1. Search for term, take top MAX_RESULTS results
     * 2. Take top result, search for related keywords, take MAX_RELATED_RESULTS
     *    and add to results
     * 3. Order results by boost score multiplied with item overall score, take
     *    MAX_RESULTS - 1
     * 4. Add top result from 1.
     * 5. Order by boost score multiplied with item overall score
     */
    const results = await elasticsearch.search({
        index: ecosystem,
        size: common_1.MAX_RESULTS,
        body: {
            query: {
                dis_max: {
                    queries: [
                        {
                            dis_max: {
                                queries: [
                                    {
                                        match: {
                                            keywords: {
                                                query: term,
                                                boost: BOOST_NAME_EXACT,
                                            },
                                        },
                                    },
                                    {
                                        wildcard: {
                                            name: {
                                                value: `${term}*`,
                                                boost: BOOST_NAME_WILDCARD,
                                            },
                                        },
                                    },
                                ],
                            },
                        },
                        {
                            match: {
                                keywords: {
                                    query: term,
                                    boost: BOOST_KEYWORD,
                                },
                            },
                        },
                        {
                            wildcard: {
                                'owner.name': {
                                    value: `${term}*`,
                                    boost: BOOST_OWNER,
                                },
                            },
                        },
                        {
                            wildcard: {
                                readme: {
                                    value: `*${term}*`,
                                    boost: BOOST_README,
                                },
                            },
                        },
                    ],
                    tie_breaker: 0.5,
                },
            },
        },
    });
    let searchResults = utils_1.parse(results, {
        reason: 'search_match',
        maxScore: MAX_SEARCH_BOOST,
    });
    let relatedMods = [];
    let mods = [];
    // Take top hit and search again for keywords
    if (searchResults.length > 0) {
        const topMod = searchResults[0];
        if (topMod.keywords.length > 0) {
            const relatedResults = await elasticsearch.search({
                index: ecosystem,
                size: MAX_RELATED_RESULTS,
                body: {
                    query: {
                        bool: {
                            must: {
                                bool: {
                                    should: (topMod.keywords || []).map((keyword) => ({
                                        term: {
                                            keywords: {
                                                value: keyword,
                                                boost: 1,
                                            },
                                        },
                                    })),
                                    minimum_should_match: 1,
                                },
                            },
                            must_not: {
                                term: {
                                    name: topMod.name,
                                },
                            },
                        },
                    },
                },
            });
            relatedMods = utils_1.parse(relatedResults, {
                reason: 'related_keywords',
                maxScore: MAX_RELATED_BOOST,
            });
        }
        mods = [...searchResults, ...relatedMods]
            .sort(byScore)
            .filter(unique)
            .slice(0, common_1.MAX_RESULTS);
        // If top search results is no longer in the mods array, add it back
        if (searchResults.length > 0 &&
            !mods.find((item) => item.name === searchResults[0].name)) {
            mods = [...mods.slice(0, common_1.MAX_RESULTS - 1), searchResults[0]].sort(byScore);
        }
    }
    return mods;
};
const stats = ({ elasticsearch, logger }) => async (ecosystem) => {
    logger.info(`[data-layer] Stats of ${ecosystem}`);
    const results = await elasticsearch.search({
        index: ecosystem,
        size: 0,
        body: {
            aggs: {
                'my-agg-name': {
                    terms: {
                        field: 'my-field',
                    },
                },
            },
        },
    });
    return results;
};
//# sourceMappingURL=adapter.js.map